import React from 'react';
import { motion } from 'framer-motion';
import { formatTime } from '../../gameEngine';

function Character({ x, y, color, flip = false }) {
  const dir = flip ? -1 : 1;
  const skinColor = '#FFCC99';
  const hatColor = color === '#1a8cff' ? '#0d5fb8' : '#cc6600';

  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Legs */}
      <line x1={-4 * dir} y1={32} x2={-8 * dir} y2={50} stroke={color} strokeWidth={5} strokeLinecap="round" />
      <line x1={6 * dir} y1={32} x2={10 * dir} y2={50} stroke={color} strokeWidth={5} strokeLinecap="round" />
      {/* Shoes */}
      <ellipse cx={-8 * dir} cy={52} rx={5} ry={3} fill="#333" />
      <ellipse cx={10 * dir} cy={52} rx={5} ry={3} fill="#333" />
      {/* Body */}
      <rect x={-10} y={5} width={20} height={28} rx={6} fill={color} />
      {/* Arms pulling rope */}
      <line x1={10 * dir} y1={12} x2={28 * dir} y2={6} stroke={skinColor} strokeWidth={5} strokeLinecap="round" />
      <line x1={-5 * dir} y1={14} x2={22 * dir} y2={2} stroke={skinColor} strokeWidth={5} strokeLinecap="round" />
      {/* Head */}
      <circle cx={0} cy={-6} r={12} fill={skinColor} />
      {/* Hat */}
      <rect x={-12} y={-20} width={24} height={10} rx={4} fill={hatColor} />
      <rect x={-15} y={-12} width={30} height={4} rx={2} fill={hatColor} />
      {/* Eyes */}
      <circle cx={-4 * dir} cy={-8} r={2} fill="#333" />
      <circle cx={4 * dir} cy={-8} r={2} fill="#333" />
      {/* Mouth - determined expression */}
      <path d={`M${-3 * dir},${-1} Q${0},${1} ${3 * dir},${-1}`} fill="none" stroke="#333" strokeWidth={1.5} />
    </g>
  );
}

export default function TugOfWarStage({
  ropePosition,
  team1Score,
  team2Score,
  team1Name,
  team2Name,
  timeLeft,
  maxPosition = 5,
  gameOver,
  language
}) {
  const offset = (ropePosition / maxPosition) * 120;
  const isUrgent = timeLeft <= 10 && timeLeft > 0;
  const team1Color = '#1a8cff';
  const team2Color = '#ff8c1a';

  return (
    <div className="tug-stage" data-testid="game-center-tug-stage">
      {/* Score display */}
      <div className="tug-scores">
        <div className="score-chip score-chip-team1" data-testid="game-team1-score">
          <span className="score-team-name">{team1Name}</span>
          <span className="score-value">{team1Score}</span>
        </div>

        <div className={`timer-pill ${isUrgent ? 'timer-urgent' : ''}`} data-testid="game-timer">
          {formatTime(timeLeft)}
        </div>

        <div className="score-chip score-chip-team2" data-testid="game-team2-score">
          <span className="score-value">{team2Score}</span>
          <span className="score-team-name">{team2Name}</span>
        </div>
      </div>

      {/* SVG Animation */}
      <div className="tug-svg-container">
        <svg viewBox="0 0 600 200" className="tug-svg" preserveAspectRatio="xMidYMid meet">
          {/* Ground shadow */}
          <ellipse cx="300" cy="175" rx="260" ry="18" fill="#d4c5a9" opacity="0.4" />

          {/* Center line */}
          <line x1="300" y1="70" x2="300" y2="160" stroke="#999" strokeWidth="1.5" strokeDasharray="4,4" opacity="0.5" />

          {/* Rope */}
          <motion.g animate={{ x: offset }} transition={{ type: 'spring', stiffness: 180, damping: 14 }}>
            <line x1="40" y1="105" x2="560" y2="105" stroke="#b8956a" strokeWidth="8" strokeLinecap="round" />
            <line x1="40" y1="105" x2="560" y2="105" stroke="#a07850" strokeWidth="3" strokeLinecap="round" opacity="0.3" />
          </motion.g>

          {/* Flag/Knot marker */}
          <motion.g
            animate={{ x: offset }}
            transition={{ type: 'spring', stiffness: 180, damping: 14 }}
          >
            <line x1="300" y1="65" x2="300" y2="108" stroke="#444" strokeWidth="3" />
            <polygon points="300,65 300,88 318,76" fill="#ef4444" />
            <polygon points="300,65 300,88 318,76" fill="#ff6b6b" opacity="0.5" />
          </motion.g>

          {/* Blue team characters (left side) */}
          <motion.g
            animate={{ x: offset * 0.4 }}
            transition={{ type: 'spring', stiffness: 150, damping: 12 }}
          >
            <Character x={90} y={95} color={team1Color} flip={false} />
            <Character x={135} y={98} color={team1Color} flip={false} />
            <Character x={180} y={95} color={team1Color} flip={false} />
          </motion.g>

          {/* Orange team characters (right side) */}
          <motion.g
            animate={{ x: offset * 0.4 }}
            transition={{ type: 'spring', stiffness: 150, damping: 12 }}
          >
            <Character x={420} y={95} color={team2Color} flip={true} />
            <Character x={465} y={98} color={team2Color} flip={true} />
            <Character x={510} y={95} color={team2Color} flip={true} />
          </motion.g>

          {/* Position indicators */}
          {Array.from({ length: 11 }, (_, i) => i - 5).map(pos => (
            <circle
              key={pos}
              cx={300 + pos * 24}
              cy="170"
              r={pos === 0 ? 4 : 3}
              fill={pos === 0 ? '#666' : '#bbb'}
              opacity={0.6}
            />
          ))}
        </svg>
      </div>

      {/* Rope progress bar */}
      <div className="rope-progress">
        <div className="rope-progress-track">
          <motion.div
            className="rope-progress-fill"
            animate={{
              left: `${50 + (ropePosition / maxPosition) * 50}%`,
            }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          />
          <div className="rope-progress-center" />
        </div>
      </div>
    </div>
  );
}
